import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  IconButton, InputLabel, MenuItem, Select, styled, Typography, RadioGroup, FormControlLabel, Radio} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExcelJS from "exceljs";
import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {toast} from "react-toastify";
import {axios_tenant} from "../../../axios";
import { normalizeCell, hasValidTemplateHeaders, TEMPLATE_MISMATCH_MESSAGE } from "./metadataTemplate";

const reasons = [
  'Not available at Source',
  'Not applicable',
  'File exceeds 1000-page limit'
];

const requiredMetadataFields = [
  {key: "inventor", label: "Inventor"},
];

// Maps a template row to the metadata payload, or returns the first validation
// failure as {message, status} so the caller can surface it and abort.
const parseMetadataRow = (metadataRow) => {
  if (typeof metadataRow[0] === 'undefined' || metadataRow[0] === null || metadataRow[0] === '') {
    return {error: {message: 'Could Not Find any Reference Number in metadata file', status: 'Error reading metadata file'}};
  }

  //insert patentNumber, country, kindCode, grantDate, applicantName, inventor
  const metadata = {
    patentNumber: metadataRow[0],
    country: metadataRow[1]?.toUpperCase(),
    kindCode: metadataRow[2]?.toUpperCase(),
    grantDate: normalizeCell(metadataRow[3]),
    applicantName: normalizeCell(metadataRow[4]),
    inventor: normalizeCell(metadataRow[5]),
    applicationFilingDate: normalizeCell(metadataRow[6]),
    applicationNumber: normalizeCell(metadataRow[7]),
    abstractOrTranslation: normalizeCell(metadataRow[8])
  };

  if (!metadata.patentNumber) {
    return {error: {message: 'Missing Reference Number', status: 'Reference Number Missing'}};
  }
  if (!metadata.country) {
    return {error: {message: 'Missing Country Code', status: 'Country Code Missing'}};
  }
  if (!metadata.kindCode) {
    return {error: {message: 'Missing Kind Code', status: 'Kind Code Missing'}};
  }

  const missingFields = requiredMetadataFields
    .filter(field => !metadata[field.key])
    .map(field => field.label);

  if (missingFields.length > 0) {
    return {
      error: {
        message: `Missing fields (${missingFields.join(", ")}) for ${metadata.country}${metadata.patentNumber}${metadata.kindCode}`,
        status: ' Please verify the missing fields'
      }
    };
  }

  return {metadata};
};

// Returns a message when the uploaded metadata describes a different reference
// than the row it is being attached to, otherwise null.
// Comparisons stay loose: spreadsheet cells arrive as numbers or strings.
const findPatentMismatch = (metadata, patent) => {
  if (metadata.country.toUpperCase() != patent.Patent.country.toUpperCase()) {
    return "Country in metadata file does not match the reference number";
  }
  if (metadata.patentNumber != patent.Patent.patentNumber) {
    return 'Patent number in metadata file does not match the reference number';
  }
  if (metadata.kindCode.toUpperCase() != patent.Patent.kindCode.toUpperCase()) {
    return "Kind code in metadata file does not match the reference number";
  }
  return null;
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadDialog = ({ open, onClose, patent, requestId, loadRequests }) => {
  const [abstractFile, setAbstractFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const [abstractReason, setAbstractReason] = useState('');
  const [abstractOrTranslation, setAbstractOrTranslation] = useState('');
  const [documentReason, setDocumentReason] = useState('');
    const refNum = patent ? patent.Patent.country + patent.Patent.patentNumber + patent.Patent.kindCode : "";
  const uploadTargetRef = useRef(null);

  const clearFileInputs = () => {
    ['abstract-file-input', 'original-document-file-input', 'metadata-file-input'].forEach((id) => {
      const input = document.getElementById(id);
      if (input) input.value = null;
    });
  };

  const resetFileState = () => {
    setUploadStatus('');
    setAbstractFile(null);
    setAbstractOrTranslation('');
    setDocumentFile(null);
    setMetadataFile(null);
    setAbstractReason('');
    setDocumentReason('');
    clearFileInputs();
  };

  const handleDeleteFile = (setFile, fileInputId) => {
    setFile(null);
    const input = document.getElementById(fileInputId);
    if (input) input.value = null;
  };


  const handleAbstractOrTranslation = (abstractOrTranslation) => {
    if (abstractOrTranslation === '' || abstractOrTranslation === 'Abstract' || abstractOrTranslation === 'Translation') {
      setAbstractOrTranslation(abstractOrTranslation);
      if(abstractOrTranslation !=''){
        setAbstractReason(''); 
      }
    } else {
      toast.error('Invalid Abstract or Translation option');
    }
  }
  // setReason is null for metadata, which has no "reason instead of a file" option.
  const handleFileChange = (e, setFile, setReason, fileInputId) => {
    if (e.target.files[0]) {
      // Check if the file is a PDF
      const acceptedFileTypes = {
        'abstract-file-input': 'application/pdf',
        'original-document-file-input': 'application/pdf',
        'metadata-file-input': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      if (e.target.files[0].type !== acceptedFileTypes[fileInputId]) {
        document.getElementById(fileInputId).value = null; // Clear the specific file input field
        toast.error(`Please upload a valid ${acceptedFileTypes[fileInputId] === 'application/pdf' ? 'PDF' : 'Excel'} file`);
        setFile(null);
        setReason?.('');
        return;
      }

      setFile(e.target.files[0]);
      setReason?.('');
    }
  };

  const handleReasonChange = (e, setReason, setFile, fileInputId) => {
    setReason(e.target.value);
    setFile(null);
    document.getElementById(fileInputId).value = null; // Clear the specific file input field
    if(fileInputId === "abstract-file-input"){
      setAbstractOrTranslation('')
    }

  };

  const readExcelFile = async (file) => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.getWorksheet(1);
    if (!hasValidTemplateHeaders(worksheet)) {
      return { headerMismatch: true };
    }
    const firstRow = worksheet.getRow(2).values;
    return { metadataRow: firstRow.slice(1) }; // ExcelJS uses 1-based indexing, so we slice to remove the empty first element
  };

  const handleCancel = () => {
    // Explicit Cancel means the user is discarding this upload attempt,
    // unlike an accidental backdrop click/Escape, which should preserve selections.
    resetFileState();
    onClose();
  };

  const failUpload = (message, status) => {
    toast.error(message);
    setUploadStatus(status);
    setLoading(false);
  };

  const handleUpload = async () => {
    setUploadStatus('Uploading...');
    setLoading(true);
    const formData = new FormData();
    formData.append('requestId', requestId);

    if (abstractFile) {
      formData.append('abstract', abstractFile);
      formData.append('abstractOrTranslation', JSON.stringify(abstractOrTranslation));
    }else if(abstractReason){
      formData.append('abstractRemarks', abstractReason);
    }

    if (documentFile) {
      formData.append('originalDocument', documentFile);
    } else if (documentReason) {
      formData.append('documentRemarks', documentReason);
    }


    if (metadataFile) {
      let parsed;
      try {
        const { metadataRow, headerMismatch } = await readExcelFile(metadataFile);
        if (headerMismatch) {
          failUpload(TEMPLATE_MISMATCH_MESSAGE, 'Metadata file does not match the template');
          return;
        }
        parsed = parseMetadataRow(metadataRow);
      } catch (error) {
        console.error('Failed to read metadata spreadsheet', error);
        failUpload('Error reading Excel file. Please check the file format.', 'Error reading metadata file');
        return;
      }

      if (parsed.error) {
        failUpload(parsed.error.message, parsed.error.status);
        return;
      }

      const mismatch = findPatentMismatch(parsed.metadata, patent);
      if (mismatch) {
        failUpload(mismatch, 'Reference mismatch');
        return;
      }

      formData.append('metadata', JSON.stringify(parsed.metadata));
    }
    if((!abstractFile && !abstractReason) && (!documentFile && !documentReason) && !metadataFile){
      setUploadStatus('Please select any file to continue.');
      setLoading(false);
      return;
    }

    try {
      await axios_tenant.post(
          `/reference-downloader/files/upload/${patent.requestPatentId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
      );
      setUploadStatus('Upload successful');
      setTimeout(() => {
        resetFileState();
        onClose();
        loadRequests();
        toast.success('Reference Updated Successfully');
      }, 500);
    } catch (error) {
      toast.error('Failed to upload files');
      setUploadStatus('Upload failed');
      console.error('Reference file upload failed', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && patent) {
      const targetKey = patent.requestPatentId;
      if (uploadTargetRef.current !== targetKey) {
        uploadTargetRef.current = targetKey;
        resetFileState();
      }
    }
  }, [open, patent]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" keepMounted>
      <DialogTitle>Upload Patent Files</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
            Reference Number: {refNum}
        </Typography>
        <Box my={4}>
              <FormControl>
                <RadioGroup
                  aria-labelledby="abstract-or-translation"
                  name="abstract-or-translation"
                  value={abstractOrTranslation}
                  onChange={(e) => handleAbstractOrTranslation(e.target.value)}
                  sx={{ display: 'flex', flexDirection: 'row' }}
                >
                  <FormControlLabel value="Abstract" control={<Radio />} label="Abstract" />
                  <FormControlLabel value="Translation" control={<Radio />} label="Translation" />
                </RadioGroup>
              </FormControl>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="file"
                  id="abstract-file-input"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, setAbstractFile, setAbstractReason, "abstract-file-input")}
                  style={{ width: 260 }}
                  disabled={loading}
                  />
                {abstractFile && (
                  <IconButton
                    size="small"
                    aria-label="Remove abstract file"
                    disabled={loading}
                    onClick={() => handleDeleteFile(setAbstractFile, "abstract-file-input")}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <InputLabel id="abstract-reason-label">Reason To Ignore</InputLabel>
                  <Select
                      labelId="abstract-reason-label"
                      value={abstractReason}
                      onChange={(e) => handleReasonChange(e, setAbstractReason, setAbstractFile, "abstract-file-input")}
                      label="Reason"
                      // disabled={!!abstractFile}
                  >
                    {reasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box my={4}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={7}>
            <Typography variant="subtitle2">Original File</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                      type="file"
                      id="original-document-file-input"
                      accept=".pdf"
                      onChange={(e) => handleFileChange(e, setDocumentFile, setDocumentReason, "original-document-file-input")}
                      disabled={loading}
                      style={{width: 260}}
                  />
                  {documentFile && (
                    <IconButton
                      size="small"
                      aria-label="Remove original file"
                      disabled={loading}
                      onClick={() => handleDeleteFile(setDocumentFile, "original-document-file-input")}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <InputLabel id="document-reason-label">Reason To Ignore</InputLabel>
                  <Select
                      labelId="document-reason-label"
                      value={documentReason}
                      onChange={(e) => handleReasonChange(e, setDocumentReason, setDocumentFile, "original-document-file-input")}
                      label="Reason"
                      // disabled={!!documentFile}
                  >
                    {reasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box my={2}>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
            <Typography variant="subtitle2">Metadata</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                      type="file"
                      id="metadata-file-input"
                      accept=".xlsx, .xls"
                      onChange={(e) => handleFileChange(e, setMetadataFile, null, "metadata-file-input")}
                      disabled={loading}
                      style={{width: 260}}
                  />
                  {metadataFile && (
                    <IconButton
                      size="small"
                      aria-label="Remove metadata file"
                      disabled={loading}
                      onClick={() => handleDeleteFile(setMetadataFile, "metadata-file-input")}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
          {uploadStatus && (
              <Typography color={uploadStatus.includes("failed") ? "error" : "primary"}>
                {uploadStatus}
              </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" color="primary" disabled={loading || (!(abstractFile && abstractOrTranslation) && !abstractReason) && (!documentFile && !documentReason) && !metadataFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
  );
};

UploadDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loadRequests: PropTypes.func,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patent: PropTypes.shape({
    requestPatentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Patent: PropTypes.shape({
      country: PropTypes.string,
      patentNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      kindCode: PropTypes.string,
    }),
  }),
};

export default UploadDialog;